import {format} from 'date-fns';
import type {ReactElement} from 'react';
import {useEffect, useMemo, useState} from 'react';

import {DatePicker} from '@/components';
import {Input, NumberInput} from '@/components/common/Input';
import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
  AfriexWalletType,
  BusinessFeeComparator,
  BusinessFeeField,
} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';

import {Select} from './Select';

type FieldValueType =
  | 'STRING'
  | 'NUMBER'
  | 'DATE'
  | 'TRANSACTION_CHANNEL'
  | 'TRANSACTION_TYPE'
  | 'CURRENCY'
  | 'COUNTRY'
  | 'PROCESSOR'
  | 'WALLET_TAG';

const mapFieldTypeToValue = (field: BusinessFeeField): FieldValueType => {
  switch (field) {
    case BusinessFeeField.FromCurrency:
    case BusinessFeeField.ToCurrency:
      return 'CURRENCY';
    case BusinessFeeField.FromCountry:
    case BusinessFeeField.ToCountry:
      return 'COUNTRY';
    case BusinessFeeField.FromProcessor:
    case BusinessFeeField.ToProcessor:
      return 'PROCESSOR';
    case BusinessFeeField.FromAmount:
    case BusinessFeeField.ToAmount:
      return 'NUMBER';
    case BusinessFeeField.FromChannel:
    case BusinessFeeField.ToChannel:
      return 'TRANSACTION_CHANNEL';
    case BusinessFeeField.TransactionType:
      return 'TRANSACTION_TYPE';
    case BusinessFeeField.WalletTag:
      return 'WALLET_TAG';
    case BusinessFeeField.TransactionDate:
      return 'DATE';
    default:
      return 'STRING';
  }
};

const valueTypeToOptionsMap: Partial<Record<FieldValueType, string[]>> = {
  CURRENCY: Object.values(AfriexCurrencyCode),
  COUNTRY: Object.values(AfriexCountryCode),
  TRANSACTION_CHANNEL: Object.values(AfriexTransactionChannel),
  TRANSACTION_TYPE: Object.values(AfriexTransactionType),
  PROCESSOR: Object.values(AfriexTransactionProcessor),
  WALLET_TAG: Object.values(AfriexWalletType),
};

const listToOptions = (list: string[]) =>
  list.map((action) => ({
    label: action,
    key: formatSelectOptionItem(action),
    value: action,
  }));

type Props = {
  field: BusinessFeeField;
  comparator: BusinessFeeComparator;
  handleValueChange: (value: string) => void;
  /** Test hook prefix for the value widget the field renders. */
  dataCy?: string;
};

export const FeeRuleValueInput = ({
  field,
  comparator,
  handleValueChange,
  dataCy,
}: Props): ReactElement => {
  const valueType = mapFieldTypeToValue(field);
  const shouldRenderTwice = comparator === BusinessFeeComparator.Between;
  const [firstValue, setFirstValue] = useState<string>('');
  const [secondValue, setSecondValue] = useState<string>('');
  const selectOptions = useMemo(() => {
    const options = valueTypeToOptionsMap[valueType] ?? [];
    const mapped = listToOptions(options);
    if (field === BusinessFeeField.WalletTag) {
      return mapped.filter((option) => option.value !== AfriexWalletType.Fee);
    }
    return mapped;
  }, [valueType, field]);

  useEffect(() => {
    const valueToUpdate = [firstValue, secondValue].filter(Boolean);

    handleValueChange(valueToUpdate.join(','));
  }, [firstValue, secondValue]);

  useEffect(() => {
    setFirstValue('');
    setSecondValue('');
  }, [valueType]);

  const handleChange = (value: string, isSecond: boolean = false) => {
    if (isSecond) {
      setSecondValue(value);
    } else {
      setFirstValue(value);
    }
  };

  const selectWidget1 = (
    <Select
      id="field"
      name="field"
      dataCy={dataCy}
      options={selectOptions}
      value={firstValue}
      className="w-full lg:w-fit"
      placeholder="---Select One---"
      onChange={(value: string) => handleChange(value)}
    />
  );
  const selectWidget2 = (
    <Select
      id="field"
      name="field"
      dataCy={dataCy ? `${dataCy}-second` : undefined}
      options={selectOptions}
      value={secondValue}
      className="w-full lg:w-fit"
      placeholder="---Select One---"
      onChange={(value: string) => handleChange(value, true)}
    />
  );

  const numberInputWidget1 = (
    <NumberInput
      min={0}
      dataCy={dataCy}
      value={firstValue}
      className="w-fit"
      onValueChange={(rawvalue: string) => handleChange(rawvalue)}
    />
  );
  const numberInputWidget2 = (
    <NumberInput
      min={0}
      dataCy={dataCy ? `${dataCy}-second` : undefined}
      value={secondValue}
      className="w-fit"
      onValueChange={(rawvalue: string) => handleChange(rawvalue, true)}
    />
  );
  // The calendar hands back local midnight on the picked day, so the day is
  // read in local time. Converting to UTC first (toISOString) saved the day
  // before anywhere ahead of UTC, Lagos included.
  const toCalendarDay = (date: Date) => format(date, 'yyyy-MM-dd');

  // react-datepicker does not forward unknown props to its input, so the test
  // hook sits on a wrapper.
  const datePicker1 = (
    <div data-cy={dataCy}>
      <DatePicker
        value={firstValue}
        setSelectedDate={(date: Date) => {
          handleChange(toCalendarDay(date));
        }}
      />
    </div>
  );
  const datePicker2 = (
    <div data-cy={dataCy ? `${dataCy}-second` : undefined}>
      <DatePicker
        value={secondValue}
        setSelectedDate={(date: Date) => {
          handleChange(toCalendarDay(date), true);
        }}
      />
    </div>
  );

  const textInputWidget1 = (
    <Input
      value={firstValue}
      className="w-fit"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(e.target.value)
      }
    />
  );
  const textInputWidget2 = (
    <Input
      value={secondValue}
      className="w-fit"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(e.target.value, true)
      }
    />
  );
  switch (valueType) {
    case 'TRANSACTION_CHANNEL':
    case 'TRANSACTION_TYPE':
    case 'CURRENCY':
    case 'COUNTRY':
    case 'PROCESSOR':
    case 'WALLET_TAG':
      return shouldRenderTwice ? (
        <>
          {selectWidget1}
          {selectWidget2}
        </>
      ) : (
        selectWidget1
      );
    case 'NUMBER':
      return shouldRenderTwice ? (
        <>
          {numberInputWidget1}
          {numberInputWidget2}
        </>
      ) : (
        numberInputWidget1
      );
    case 'DATE':
      return shouldRenderTwice ? (
        <>
          {datePicker1}
          {datePicker2}
        </>
      ) : (
        datePicker1
      );
    default:
      return shouldRenderTwice ? (
        <>
          {textInputWidget1}
          {textInputWidget2}
        </>
      ) : (
        textInputWidget1
      );
  }
};
